import { useState } from 'react';

import { useAuthStore } from '@/features/auth/auth-store';
import { canActOnReferrals } from '@/features/auth/roles';
import {
  REFERRAL_STATUS_META,
  type Referral,
  effectiveOwner,
  isMine,
  timeAgoLabel,
  useReferralsStore,
} from '@/features/referrals/referrals-store';

/** Scope of the referral list — mirrors the v6 `dashRefScope` state. */
type RefScope = 'mine' | 'all';

export interface ReferralsCardProps {
  /** Navigate to the Referrals page (View all → / row click). */
  onOpenReferrals: (assignedToMe: boolean) => void;
  /** "Start outreach": open Campaigns with the referral's customer + product staged. */
  onStartOutreach: (referral: Referral) => void;
}

/**
 * The Dashboard "Referrals" hero card (SCRUM-327) — v6 `.dash-attention`:
 * gold-tinted border, scope tab pills (default "Assigned to me"), the 5 most
 * recent referrals with status dots and per-row actions (Start outreach /
 * Unassign on actionable rows; a status label on terminal rows), and the
 * View all → link that opens the Referrals page pre-scoped.
 */
export function ReferralsCard({ onOpenReferrals, onStartOutreach }: ReferralsCardProps) {
  const [scope, setScope] = useState<RefScope>('mine');
  const referrals = useReferralsStore((s) => s.referrals);
  const approve = useReferralsStore((s) => s.approve);
  const release = useReferralsStore((s) => s.release);
  const user = useAuthStore((s) => s.user);
  // v7 role variants (SCRUM-388): actors keep the scope toggle + row actions;
  // tellers see their OWN submissions with status sublines and no actions.
  const canAct = canActOnReferrals(user?.role ?? 'Bank Executive');
  const isTeller = !canAct;

  const mineRefs = referrals.filter(
    (r) => (r.status === 'pending' || r.status === 'reviewed') && isMine(r, user?.email),
  );
  const tellerRefs = referrals.filter((r) => r.tellerName === (user?.displayName ?? ''));
  const rows = (isTeller ? tellerRefs : scope === 'mine' ? mineRefs : [...referrals])
    .sort((a, b) => a.submittedDaysAgo - b.submittedDaysAgo)
    .slice(0, 5);

  return (
    <section
      aria-label="Referrals"
      className="bg-bg-4 rounded-tk-lg border p-4"
      style={{ borderColor: 'color-mix(in srgb, var(--cyan) 28%, var(--border-0))' }}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="font-display text-text-0 text-base font-semibold">Referrals</span>
        <button
          type="button"
          onClick={() => {
            onOpenReferrals(scope === 'mine');
          }}
          className="text-brand-primary text-[0.8125rem] font-medium hover:underline"
        >
          View all →
        </button>
      </div>

      {/* Scope tabs (actors only — v7 tellers get no toggle). */}
      {canAct && (
        <div className="bg-bg-2 border-border-0 rounded-tk-md mb-3 inline-flex gap-0.5 border p-[3px]">
          <ScopeTab
            label="Assigned to me"
            count={mineRefs.length}
            active={scope === 'mine'}
            onClick={() => {
              setScope('mine');
            }}
          />
          <ScopeTab
            label="All referrals"
            count={referrals.length}
            active={scope === 'all'}
            onClick={() => {
              setScope('all');
            }}
          />
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState scope={scope} isTeller={isTeller} />
      ) : (
        <div className="flex flex-col">
          {rows.map((r) => (
            <ReferralRow
              key={r.id}
              referral={r}
              scope={scope}
              isTeller={isTeller}
              onStartOutreach={() => {
                approve(r.id);
                onStartOutreach(r);
              }}
              onRelease={() => {
                release(r.id, user?.displayName ?? 'You');
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ScopeTab({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-[5px] text-[0.8125rem] font-medium transition-colors ${
        active ? 'bg-bg-4 text-text-0 shadow-sm' : 'text-text-2 hover:text-text-0'
      }`}
    >
      {label}
      <span
        className={`rounded-tk-full min-w-[18px] px-1.5 py-px text-center font-mono text-[0.625rem] font-bold ${
          active ? 'text-brand-primary bg-brand-dim' : 'text-text-3 bg-bg-4 border-border-1 border'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

const DOT_CLASS: Record<string, string> = {
  pending: 'bg-warning',
  reviewed: 'bg-brand-primary',
  success: 'bg-status-success',
  denied: 'bg-status-denied',
};

function ReferralRow({
  referral: r,
  scope,
  isTeller,
  onStartOutreach,
  onRelease,
}: {
  referral: Referral;
  scope: RefScope;
  isTeller: boolean;
  onStartOutreach: () => void;
  onRelease: () => void;
}) {
  const meta = REFERRAL_STATUS_META[r.status];
  const owner = effectiveOwner(r);
  // v7 teller rows are read-only trackers: status subline, no actions.
  const actionable = !isTeller && (r.status === 'pending' || r.status === 'reviewed');
  const context = isTeller
    ? meta.label
    : scope === 'mine'
      ? `from ${r.tellerName}`
      : owner.isDefault
        ? `${owner.name} · default`
        : owner.name;

  // Deliberately NOT interactive: the row contains real buttons (Start
  // outreach / Unassign), and interactive descendants inside a role="button"
  // are invalid ARIA — the nesting also broke E2E name-matching (rows counted
  // as "Start outreach" buttons). The v6 row-click (open the customer profile
  // on the Referrals console) arrives with SCRUM-329 where its target exists;
  // until then "View all →" is the navigation path.
  return (
    <div className="border-border-0 hover:bg-bg-3 flex items-center gap-3 border-t px-1.5 py-2.5 transition-colors first:border-t-0">
      <span className={`h-2 w-2 flex-none rounded-full ${DOT_CLASS[meta.tint] ?? 'bg-text-3'}`} />
      <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
        <span className="text-text-0 text-sm font-medium">{r.customerName}</span>
        <span className="text-text-3 truncate text-xs">
          {r.productName} · {context} · {timeAgoLabel(r.submittedDaysAgo)}
        </span>
      </span>
      {actionable ? (
        <span className="flex flex-none items-center gap-1.5">
          {r.assignee && (
            <button
              type="button"
              title="Release back to the Branch Manager queue"
              onClick={(e) => {
                e.stopPropagation();
                onRelease();
              }}
              className="text-text-2 border-border-1 hover:bg-bg-3 hover:text-text-0 hover:border-border-2 rounded-md border bg-transparent px-2.5 py-[5px] text-xs font-medium transition-colors"
            >
              Unassign
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onStartOutreach();
            }}
            className="text-brand-primary rounded-md px-2.5 py-[5px] text-xs font-semibold transition-colors"
            style={{
              background: 'color-mix(in srgb, var(--cyan) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--cyan) 30%, transparent)',
            }}
          >
            Start outreach
          </button>
        </span>
      ) : (
        <span className="flex flex-none items-center gap-1.5 text-xs font-medium">
          <span className={`h-[7px] w-[7px] rounded-full ${DOT_CLASS[meta.tint] ?? 'bg-text-3'}`} />
          <span className={r.status === 'denied' ? 'text-text-3' : 'text-text-1'}>
            {meta.label}
          </span>
        </span>
      )}
    </div>
  );
}

function EmptyState({ scope, isTeller }: { scope: RefScope; isTeller: boolean }) {
  return (
    <div className="flex items-center gap-3.5 px-1 py-3">
      <svg
        viewBox="0 0 48 40"
        width="46"
        height="38"
        fill="none"
        stroke="var(--text-3)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M8 12l16 11 16-11" />
        <rect x="8" y="9" width="32" height="22" rx="3" />
      </svg>
      <p className="text-text-2 text-sm leading-normal">
        {isTeller
          ? 'You haven’t submitted any referrals yet. Refer a customer from their profile and you can track it here.'
          : scope === 'mine'
            ? 'You’re all caught up — no referrals are assigned to you right now.'
            : 'No referrals yet. They’ll appear here as your team submits them.'}
      </p>
    </div>
  );
}
